/**
 * Currency Helpers
 * -----------------------------------------------------------------------------
 * A collection of useful functions that help with currency formatting
 *
 * Current contents
 * - formatMoney - Takes an amount in cents and returns it as a formatted dollar value.
 *
 */

const moneyFormat = '${{amount}}';

/**
 * Format money values based on your shop currency settings
 * @param  {Number|string} cents - value in cents or dollar amount e.g. 300 cents
 * or 3.00 dollars
 * @param  {String} format - shop money_format setting
 * @return {String} value - formatted value
 */
function formatMoney(cents, format) {
  if (typeof cents === 'string') {
    cents = cents.replace('.', '');
  }
  let value = '';
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  const formatString = format || moneyFormat;

  function formatWithDelimiters(
    number,
    precision = 2,
    thousands = ',',
    decimal = '.'
  ) {
    if (isNaN(number) || number == null) {
      return 0;
    }

    number = (number / 100.0).toFixed(precision);

    const parts = number.split('.');
    const dollarsAmount = parts[0].replace(
      /(\d)(?=(\d\d\d)+(?!\d))/g,
      `$1${thousands}`
    );
    const centsAmount = parts[1] ? decimal + parts[1] : '';

    return dollarsAmount + centsAmount;
  }

  switch (formatString.match(placeholderRegex)[1]) {
    case 'amount':
      value = formatWithDelimiters(cents, 2);
      break;
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
  }

  return formatString.replace(placeholderRegex, value);
}

class ProductPrice extends HTMLElement {
  hasPopularAddOns = this.hasAttribute('data-synced-with-product-add-ons') || false;

  connectedCallback() {
    this.unsubscribeVariantChange = subscribe(
      `variant:changed:${this.dataset.productId}`,
      ({ html }) => {
        this.handlePriceChange(html);
        if (this.hasPopularAddOns && window.popularAddOns.length > 0) {
          const addOnValue = parseInt(
            window.popularAddOns.reduce((total, item) => total + item.price, 0)
          );
          this.updatePriceWith(addOnValue);
        }
      }
    );

    // Make sure the product is a add-on
    if (!this.hasPopularAddOns) return;

    this.unsubscribePriceChanged = subscribe(`price:changed:product-add-ons`, ({ cents }) => {
      this.savedCents = cents;
      this.updatePriceWith(cents);
    });
  }

  disconnectedCallback() {
    this.unsubscribeVariantChange();
    if (this.hasPopularAddOns) {
      this.unsubscribePriceChanged();
    }
  }

  /**
   * Handle price change.
   * @param {HTMLElement} html - The HTML to render.
   */
  handlePriceChange(html) {
    const productPrice = html.querySelector('product-price');
    this.innerHTML = productPrice.innerHTML;
  }

  updatePriceWith(cents) {
    // Only update the price if the cents are set in the data attribute
    if (!this.dataset.cents) return;

    this.querySelector('[data-current-price]').textContent = formatMoney(
      cents + parseInt(this.dataset.cents),
      window.SHOP_MONEY_FORMAT
    );
  }
}

customElements.define('product-price', ProductPrice);
